import React, { useEffect, useMemo } from 'react';
import { Form, Input, Select, Row, Col, App } from 'antd';
import { trpc } from '../../lib/api';
import type { CategoryLike } from '../../utils/categoryDisplay';
import { katakanaRule } from '@/constants/validation';
import { LocationTreeSelect } from '../common/LocationTreeSelect';
import { withDevModalFilePath, NumericInput, SingleUserSelect, RichTextEditor } from '../common';
import { useFormAutoBackup } from '../../hooks/useFormAutoBackup';

const { Modal } = withDevModalFilePath('client/src/components/parts/PartFormModal.tsx');
const TAX_INCLUDED_UNIT_PRICE_HELP = '税込金額を入力してください。発注書ではこの単価に消費税を追加しません。';

interface SupplierSummary {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
}

interface Part {
  id?: string;
  name?: string;
  nameKana?: string | null;
  partNumber?: string | null;
  serialNumber?: string | null;
  specifications?: string | null;
  manufacturerSupplierId?: string | null;
  manufacturerSupplier?: SupplierSummary | null;
  supplierId?: string | null;
  purchaseUrl?: string | null;
  unitPrice?: string | null;
  purchaseLotSize?: number | null;
  purchaseOverhead?: string | null;
  unit?: string | null;
  locationId?: string | null;
  storageLocation?: string | null;
  optimalStockLevel?: number | null;
  category?: CategoryLike;
  managerId?: string | null;
  notes?: string | null;
}

interface PartFormModalProps {
  open: boolean;
  part: Part | null;
  onClose: () => void;
  onSuccess: () => void;
  initialLocationId?: string | null;
  initialCategoryId?: string | null;
}

interface PartFormValues extends Record<string, unknown> {
  name: string;
  nameKana?: string;
  partNumber?: string;
  serialNumber?: string;
  specifications?: string;
  manufacturerSupplierId?: string;
  supplierId?: string;
  purchaseUrl?: string;
  unitPrice?: number;
  purchaseLotSize?: number;
  purchaseOverhead?: number;
  unit?: string;
  locationId?: string;
  storageLocation?: string;
  optimalStockLevel?: number;
  category?: string;
  managerId?: string;
  notes?: string;
}

const resolveCategoryId = (category: CategoryLike): string | undefined => {
  if (typeof category === 'string') {
    return category;
  }
  if (category && typeof category === 'object') {
    const candidate = (category as { id?: unknown }).id;
    return typeof candidate === 'string' ? candidate : undefined;
  }
  return undefined;
};

export const PartFormModal: React.FC<PartFormModalProps> = ({
  open,
  part,
  onClose,
  onSuccess,
  initialLocationId,
  initialCategoryId,
}) => {
  const [form] = Form.useForm<PartFormValues>();
  const { message } = App.useApp();

  // 編集モードかどうか
  const isEditMode = !!part;

  // フォーム自動バックアップ（新規作成のみ有効）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({    
    formType: 'part',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: open && !isEditMode,
  });

  // 業者一覧取得
  const { data: suppliersData } = trpc.suppliers.list.useQuery({
    page: 1,
    limit: 100,
  });

  // 部品・消耗品カテゴリ一覧取得（カテゴリ管理ページで管理されているもの）
  const { data: partCategories = [] } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const categoryOptions = useMemo(() => {
    return partCategories.map((cat) => ({
      label: cat.categoryLabel,
      value: cat.id, // IDを使用
    }));
  }, [partCategories]);

  const suppliers = suppliersData?.items || [];

  // 作成mutation
  const createMutation = trpc.parts.create.useMutation({
    onSuccess: () => {
      message.success('部品・消耗品を登録しました');
      form.resetFields();
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`登録に失敗しました: ${error.message}`);
    },
  });

  // 更新mutation
  const updateMutation = trpc.parts.update.useMutation({
    onSuccess: () => {
      message.success('部品・消耗品を更新しました');
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    },
  });

  // フォーム初期値設定
  useEffect(() => {
    if (open) {
      if (part) {
        // 編集モード
        form.setFieldsValue({
          name: part.name,
          nameKana: part.nameKana || undefined,
          partNumber: part.partNumber || undefined,
          serialNumber: part.serialNumber || undefined,
          specifications: part.specifications || undefined,
          manufacturerSupplierId: part.manufacturerSupplierId || undefined,
          supplierId: part.supplierId || undefined,
          purchaseUrl: part.purchaseUrl || undefined,
          unitPrice: part.unitPrice ? parseFloat(part.unitPrice) : undefined,
          purchaseLotSize: part.purchaseLotSize || 1,
          purchaseOverhead: part.purchaseOverhead ? parseFloat(part.purchaseOverhead) : undefined,
          unit: part.unit || '個',
          locationId: part.locationId || undefined,
          storageLocation: part.storageLocation || undefined,
          optimalStockLevel: part.optimalStockLevel || 0,
          category: resolveCategoryId(part.category),
          managerId: part.managerId || undefined,
          notes: part.notes || undefined,
        });
      } else {
        // 新規作成モード
        form.resetFields();
        form.setFieldsValue({
          purchaseLotSize: 1,
          unit: '個',
          optimalStockLevel: 0,
          locationId: initialLocationId || undefined,
          category: initialCategoryId || undefined,
        });
      }
    }
  }, [open, part, form, initialCategoryId, initialLocationId]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  // 送信処理
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (part) {
        // 更新
        updateMutation.mutate({
          id: part.id,
          ...values,
        });
      } else {
        // 作成
        createMutation.mutate(values);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={part ? '部品・消耗品編集' : '新規部品・消耗品登録'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={createMutation.isPending || updateMutation.isPending}
      width={800}
      okText={part ? '更新' : '登録'}
      cancelText="キャンセル"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          purchaseLotSize: 1,
          unit: '個',
          optimalStockLevel: 0,
        }}
      >
        <Row gutter={16}>
          {/* 基本情報 */}
          <Col span={24}>
            <h3>基本情報</h3>
          </Col>

          <Col span={12}>
            <Form.Item
              label="部品・消耗品名"
              name="name"
              rules={[{ required: true, message: '部品・消耗品名を入力してください' }]}
            >
              <Input placeholder="例: ガスケット 25A" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="フリガナ"
              name="nameKana"
              rules={[
                { max: 255, message: '255文字以内で入力してください' },
                katakanaRule(),
              ]}
            >
              <Input placeholder="例: ガスケット" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="型式" name="partNumber">
              <Input placeholder="例: GSK-25A" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="製造番号" name="serialNumber">
              <Input placeholder="例: SN-123456" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="定格（仕様）" name="specifications">
              <Input placeholder="例: 25A, 耐熱200℃" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="メーカー（業者）" name="manufacturerSupplierId">
              <Select
                placeholder="メーカー業者を選択"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={suppliers.map((s) => ({
                  label: s.name,
                  value: s.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="カテゴリ"
              name="category"
              rules={[{ required: true, message: 'カテゴリを選択してください' }]}
            >
              <Select
                placeholder="カテゴリを選択"
                allowClear
                showSearch
                options={categoryOptions}
              />
            </Form.Item>
          </Col>

          {/* 購入情報 */}
          <Col span={24}>
            <h3 style={{ marginTop: 16 }}>購入情報</h3>
          </Col>

          <Col span={12}>
            <Form.Item label="購入先（業者）" name="supplierId">
              <Select
                placeholder="業者を選択"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={suppliers.map(s => ({
                  label: s.name,
                  value: s.id,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="購入URL" name="purchaseUrl">
              <Input placeholder="https://..." />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="単価(税込)"
              name="unitPrice"
              extra={TAX_INCLUDED_UNIT_PRICE_HELP}
            >
              <NumericInput
                placeholder="例: 1,200（税込）"
                min={0}
                style={{ width: '100%' }}
                addonBefore="¥"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="購入ロット数" name="purchaseLotSize">
              <NumericInput placeholder="1" min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="単位" name="unit">
              <Select
                placeholder="単位を選択"
                options={[
                  { label: '個', value: '個' },
                  { label: '本', value: '本' },
                  { label: 'セット', value: 'セット' },
                  { label: 'kg', value: 'kg' },
                  { label: 'L', value: 'L' },
                  { label: 'm', value: 'm' },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="購入時間接費（送料等）" name="purchaseOverhead">
              <NumericInput
                placeholder="0"
                min={0}
                style={{ width: '100%' }}
                addonBefore="¥"
              />
            </Form.Item>
          </Col>

          {/* 在庫管理 */}
          <Col span={24}>
            <h3 style={{ marginTop: 16 }}>在庫管理</h3>
          </Col>

          <Col span={12}>
            <Form.Item
              label="保管エリア"
              name="locationId"
              tooltip="拠点内のどのエリアに保管されているか選択してください"
            >
              <LocationTreeSelect placeholder="エリアを選択" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="保管場所（補足）"
              name="storageLocation"
              tooltip="エリア内での詳細な位置（例: 棚A-1）を任意で入力できます"
            >
              <Input placeholder="例: 棚A-1（任意）" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="適正在庫数" name="optimalStockLevel">
              <NumericInput placeholder="0" min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          {/* その他 */}
          <Col span={24}>
            <h3 style={{ marginTop: 16 }}>その他</h3>
          </Col>

          <Col span={12}>
            <Form.Item label="管理ユーザー" name="managerId">
              <SingleUserSelect
                placeholder="管理ユーザーを選択"
                showAssignMeButton
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="詳細"
              name="notes"
              getValueFromEvent={(markdown: string) => markdown}
              getValueProps={(value) => ({ value: value || '' })}
            >
              <RichTextEditor
                placeholder="詳細を入力してください..."
                minHeight={120}
                showPreviewToggle={false}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PartFormModal;
